import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Loader2 } from 'lucide-react';

export default function LogList({ refreshTrigger }) {
    const { t } = useTranslation();
    const [logs, setLogs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const fetchLogs = async () => {
        setLoading(true);
        try {
            const response = await fetch('http://localhost:3001/api/logs/?limit=50');
            if (!response.ok) throw new Error('Failed to fetch logs');
            const result = await response.json();
            const data = result.data || [];
            // Sort by id descending (newest first)
            const sorted = data.sort((a, b) => b.id - a.id);
            setLogs(sorted);
            setError(null);
        } catch (error) {
            console.error('Error fetching logs:', error);
            setError('Failed to load logs.');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchLogs();
    }, [refreshTrigger]);

    const getPriorityColor = (priority) => {
        switch (priority) {
            case 'Critical': return 'destructive';
            case 'High': return 'destructive';
            case 'Medium': return 'secondary';
            case 'Low': return 'outline';
            default: return 'default';
        }
    };

    const handleSync = async () => {
        try {
            const response = await fetch('http://localhost:3001/api/sync/living-log', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({}),
            });
            const result = await response.json();
            alert(`Sync Complete!\nAdded: ${result.added}\nUpdated: ${result.updated}`);
            fetchLogs(); // Refresh the list
        } catch (error) {
            console.error('Sync error:', error);
            alert("Failed to sync living log.");
        }
    };

    if (loading && logs.length === 0) {
        return (
            <div className="flex justify-center p-8">
                <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
            </div>
        );
    }

    if (error) {
        return <div className="text-center text-red-500 p-4">{error}</div>;
    }

    return (
        <div className="space-y-4 w-full duration-500">
            <div className="flex justify-between items-center px-1">
                <h3 className="text-xl font-semibold">{t('recent_logs', 'Recent Logs')}</h3>
                <button onClick={handleSync} className="text-xs border px-2 py-1 rounded hover:bg-gray-100 dark:hover:bg-zinc-800">
                    {t('sync_external', 'Sync External')}
                </button>
            </div>
            {logs.length === 0 ? (
                <Card>
                    <CardContent className="p-8 text-center text-gray-500 dark:text-gray-400">
                        {t('no_data', 'No data available')}
                    </CardContent>
                </Card>
            ) : (
                logs.map((log) => (
                    <Card key={log.id} className="overflow-hidden transition-all hover:shadow-md">
                        <CardHeader className="p-4 pb-2">
                            <div className="flex justify-between items-start">
                                <div className="space-y-1">
                                    <Badge variant="outline" className="mb-2 mr-2 text-xs font-normal">
                                        {log.category}
                                    </Badge>
                                    <CardTitle className="text-base font-semibold leading-tight">
                                        {log.title}
                                    </CardTitle>
                                </div>
                                <Badge variant={getPriorityColor(log.priority)}>
                                    {log.priority}
                                </Badge>
                            </div>
                            <CardDescription className="text-xs mt-1">
                                {log.location} • {log.status || 'Open'}
                            </CardDescription>
                        </CardHeader>
                        <CardContent className="p-4 pt-2 text-sm text-gray-600">
                            <p className="line-clamp-3">{log.content}</p>
                        </CardContent>
                    </Card>
                ))
            )}
        </div>
    );
}